import { useCallback, useEffect, useState } from 'react';

import { useStoreContext } from 'features/store';

import { RestaurantStatus, RestaurantStatusChecker } from './types';

/**
 * useRestaurantStatusChecker - A custom React hook to periodically check the
 * availability status of a restaurant.
 *
 * This hook manages the state of the restaurant's availability status and
 * allows the caller to start a periodic check of the restaurant's availability.
 * It immediately checks the status upon starting and then continues to check
 * the status at 30-second intervals.
 *
 * The availability status can be one of the following:
 * - 'LOADING': Indicates that the availability check is in progress.
 * - 'AVAILABLE': Indicates that the restaurant is currently available for
 *   ordering.
 * - 'UNAVAILABLE': Indicates that the restaurant is currently unavailable for
 *   ordering.
 *
 * The hook returns an object with the current `restaurantStatus` and a function
 * `startRestaurantStatusChecker` that can be called to enable the periodic
 * status checking.
 *
 */
export function useRestaurantStatusChecker(): RestaurantStatusChecker {
  const { isRestaurantAvailable, store } = useStoreContext();
  const [isCheckerEnabled, setIsCheckerEnabled] = useState(false);
  const [restaurantStatus, setRestaurantStatus] = useState<RestaurantStatus>('LOADING');

  useEffect(() => {
    if (!isCheckerEnabled) {
      return;
    }

    const checkAvailability = async () => {
      setRestaurantStatus('LOADING');
      const res = await isRestaurantAvailable(store);
      if (res) {
        setRestaurantStatus('AVAILABLE');
      } else {
        setRestaurantStatus('UNAVAILABLE');
      }
    };

    checkAvailability();

    const intervalId = setInterval(checkAvailability, 30_000);
    return () => clearInterval(intervalId);
  }, [isCheckerEnabled, isRestaurantAvailable, store]);

  // Only call setIsCheckerEnabled if the checker is not already enabled. This
  // prevents unnecessary re-renders.
  const startRestaurantStatusChecker = useCallback(
    () => !isCheckerEnabled && setIsCheckerEnabled(true),
    [isCheckerEnabled]
  );

  return {
    startRestaurantStatusChecker,
    restaurantStatus,
  };
}
